/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  formatPasskeyTimestamp,
  getPasskeyOtpExitModalCopy,
  isPasskeyOtpExitModalEnabled,
  passkeyDeregisterFailed,
  passkeyDeregisterRequest,
  passkeyDeregisterSuccess,
  passkeyListRequest,
  passkeyStatusRequest,
  resetPasskeyDeregisterState,
  resetPasskeyList,
  resetPasskeyOtpGenerate,
  resetPasskeyOtpValidate,
  resetPasskeyPreferences,
  resolvePasskeyFeatureFlags,
  resolvePasskeyProviderIcon,
  RootState,
} from '@rb-serve-app/core';
import Breadcrumbs from '../../components/BreadCrumbs';
import CommanModal from '../../components/CommanModal';
import SuccessModel from '../../components/SuccessModel';
import {
  PasskeyAppleIcon,
  PasskeyInfoIcon,
  PasskeyTrashIcon,
} from '../../assets/svg/PasskeyIcons';
import PasskeyVerifyIdentityModal from './PasskeyVerifyIdentityModal';
import PasskeyVerificationCodeModal from './PasskeyVerificationCodeModal';
import { setPasskeyHintAbsent } from '../SignIn/passkeyDeviceHint';

const SUPPORT_LINK_FALLBACKS = [
  {
    label: 'iOS support',
    url: 'https://support.apple.com/guide/iphone/use-passkeys-to-sign-in-to-apps-and-websites-iphf688ee8f6/ios',
  },
  {
    label: 'Android support',
    url: 'https://support.google.com/android/answer/14124480',
  },
  {
    label: 'Google Chrome support',
    url: 'https://support.google.com/chrome/answer/13168025',
  },
  {
    label: 'Microsoft Edge support',
    url: 'https://support.microsoft.com/windows/passkeys-in-windows-577752c2-ce10-43d6-8f14-ef163e576c56',
  },
  {
    label: 'Safari support',
    url: 'https://support.apple.com/guide/safari/sign-in-with-passkeys-ibrw83720ce4/mac',
  },
];

const FAQ_FALLBACKS = [
  {
    question: 'Is my biometric information safe?',
    answer:
      "Yes. Biometric information used for registration and authentication stays on your device. It's never shared with, nor collected by, Rogers or sent to any remote server.",
  },
  {
    question: 'How do I use passkeys on different devices?',
    answer:
      'Passkeys will automatically be available on all of your synced devices. When logging in with not-synced devices, scan the QR code or use your password to get access to your account.',
  },
  {
    question: 'Can I share passkeys with friends and family?',
    answer:
      'Yes. Friends and family can create their own passkeys, if they need access to your account.',
  },
];

const resolveAsset = (
  assets: Record<string, string> | undefined,
  name?: string
): string => {
  if (!name) {
    return '';
  }
  if (name.startsWith('http') || name.startsWith('//')) {
    return name.startsWith('//') ? `https:${name}` : name;
  }
  return assets?.[name] ?? '';
};

const ManagePasskey = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userName, accountId, customerId, userCredentials } = useSelector(
    (state: RootState) => state.user
  );
  const credentialRecord = userCredentials as
    | { username?: string; userName?: string }
    | undefined;
  const otpUserName =
    (typeof userName === 'string' && userName.trim()) ||
    (typeof credentialRecord?.username === 'string' &&
      credentialRecord.username.trim()) ||
    (typeof credentialRecord?.userName === 'string' &&
      credentialRecord.userName.trim()) ||
    '';
  const {
    loading: deregisterLoading,
    success,
    error,
    otpRequired,
    passkeyToken,
  } = useSelector((state: RootState) => state.passkeyDeregister);
  const {
    loading: listLoading,
    activePasskeys,
    userId,
  } = useSelector((state: RootState) => state.passkeyList);
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData ?? {};
  const webPasskeyFlags = resolvePasskeyFeatureFlags(
    entries?.rb_feature_flagging?.featureFlagging
  ).web;
  const deletionAllowed = webPasskeyFlags.deletionEnabled;

  const rbPasskey = entries?.rb_passkey ?? entries?.rb_more?.rbPasskey ?? {};
  const exitCopy = getPasskeyOtpExitModalCopy(rbPasskey);
  const canConfirmOtpExit =
    Boolean(exitCopy) && isPasskeyOtpExitModalEnabled(rbPasskey);
  const manage = rbPasskey.managePasskey ?? {};
  const passkeys = manage.passkeys ?? {};
  const moreAbout = manage.moreAboutPasskeys ?? {};
  const showMoreAbout = moreAbout.moreAboutEnabled === true;
  const showCancelButton = manage.cancelButtonWeb === true;
  const showInfoIcon = manage.infoIconEnabled === true;
  const infoIconContent =
    typeof manage.infoIconContent === 'string'
      ? manage.infoIconContent.trim()
      : '';
  const faqItems =
    Array.isArray(moreAbout.items) && moreAbout.items.length > 0
      ? moreAbout.items
      : FAQ_FALLBACKS;
  const deleteModal = manage.deleteModal ?? {};
  const deleteActions = deleteModal.actions ?? {};
  const resultModal = manage.modal ?? {};
  const descriptions = Array.isArray(manage.description)
    ? manage.description
    : [];

  const externalLinkIconUri = resolveAsset(assets, 'external_link_icon');

  const supportLinks = (
    Array.isArray(deleteModal.supportLinks) &&
    deleteModal.supportLinks.length > 0
      ? deleteModal.supportLinks
      : SUPPORT_LINK_FALLBACKS.map((item) => ({
          label: item.label,
          link: item.url,
        }))
  ).map((item: { label?: string; link?: string }, index: number) => ({
    label: item.label ?? SUPPORT_LINK_FALLBACKS[index]?.label ?? '',
    url:
      item.link && item.link.trim().length > 0
        ? item.link
        : SUPPORT_LINK_FALLBACKS[index]?.url ?? '',
  }));

  const [otpStep, setOtpStep] = useState<'verify' | 'code' | null>(null);
  const [selectedContact, setSelectedContact] = useState('');
  const [preferenceType, setPreferenceType] = useState('');
  const [selectedPasskeyId, setSelectedPasskeyId] = useState('');
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [expandedFaqs, setExpandedFaqs] = useState<number[]>([0, 1, 2]);
  const [isDeregisterPending, setIsDeregisterPending] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);

  const passkeysTitle = passkeys.title ?? 'Your passkeys';
  const dateCreatedLabel = passkeys.dateCreated ?? 'Date created';
  const lastUsedLabel = passkeys.lastUsed ?? 'Last used';
  const keyDetailsLabel = manage.keyDetailsButton ?? 'Key details';
  const cancelLabel = manage.cancelButton ?? 'Cancel';
  const removeLabel = deleteActions.removePasskey ?? 'Remove passkey';
  const managePasskeyNavTitle =
    entries?.rb_navigation?.moreMenu?.manage_passkey || 'Manage Passkeys';

  const breadcrumbs = [
    { name: entries?.rb_navigation?.moreLogoTitle || 'More', href: '/' },
    {
      name: managePasskeyNavTitle,
      href: '/manage-passkey',
    },
  ];

  useEffect(() => {
    const featureFlagging = entries?.rb_feature_flagging?.featureFlagging;
    if (featureFlagging && !deletionAllowed) {
      navigate(-1);
    }
  }, [deletionAllowed, entries, navigate]);

  useEffect(() => {
    document.body.classList.add('manage-passkey-page');
    return () => {
      document.body.classList.remove('manage-passkey-page');
    };
  }, []);

  useEffect(() => {
    if (!deletionAllowed || !accountId || !customerId) {
      return;
    }
    dispatch(
      passkeyListRequest({
        apiName: 'passkey_list',
        data: {
          accountId,
          customerId,
          flow: 'PASSKEY_LIST_API_FLOW',
        },
      })
    );
  }, [accountId, customerId, deletionAllowed, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetPasskeyDeregisterState());
      dispatch(resetPasskeyPreferences());
      dispatch(resetPasskeyOtpGenerate());
      dispatch(resetPasskeyOtpValidate());
      dispatch(resetPasskeyList());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!otpRequired || !passkeyToken || !otpUserName) {
      return;
    }
    dispatch(resetPasskeyOtpGenerate());
    dispatch(resetPasskeyOtpValidate());
    setOtpStep('verify');
  }, [otpRequired, passkeyToken, otpUserName, dispatch]);

  const handleConfirmRemove = (): void => {
    setRemoveModalVisible(false);
    if (!deletionAllowed) {
      return;
    }
    if (!userId || !selectedPasskeyId) {
      dispatch(
        passkeyDeregisterFailed({
          statusCode: '500',
          error: {
            errCode: 'UNKNOWN_ERROR',
            msg: 'Unable to remove passkey.',
          },
        })
      );
      return;
    }
    dispatch(
      passkeyDeregisterRequest({
        apiName: 'passkey_deregister',
        data: {
          userId,
          passkeyId: selectedPasskeyId,
          flow: 'PASSKEY_DEREGISTER_API_FLOW',
        },
      })
    );
  };

  const handleSendCodeSuccess = useCallback(
    (contact: string, type: string): void => {
      setSelectedContact(contact);
      setPreferenceType(type);
      setOtpStep('code');
    },
    []
  );

  const handleOtpValidationBack = (): void => {
    dispatch(resetPasskeyOtpValidate());
    dispatch(resetPasskeyOtpGenerate());
    setOtpStep('verify');
  };

  const handleOtpClose = useCallback((): void => {
    setExitModalVisible(false);
    dispatch(resetPasskeyDeregisterState());
    dispatch(resetPasskeyOtpGenerate());
    dispatch(resetPasskeyOtpValidate());
    dispatch(resetPasskeyPreferences());
    setOtpStep(null);
  }, [dispatch]);

  const requestOtpExit = useCallback((): void => {
    if (canConfirmOtpExit) {
      setExitModalVisible(true);
      return;
    }
    handleOtpClose();
  }, [canConfirmOtpExit, handleOtpClose]);

  const handleOtpVerified = useCallback(() => {
    setOtpStep(null);
    dispatch(passkeyDeregisterSuccess({}));
  }, [dispatch]);

  useEffect(() => {
    if (success || error) {
      setIsDeregisterPending(false);
    }
    if (success && otpUserName) {
      void setPasskeyHintAbsent(otpUserName);
    }
  }, [success, error, otpUserName]);

  const handleSuccessClose = (): void => {
    if (accountId && customerId) {
      dispatch(passkeyStatusRequest({ accountId, customerId }));
    }
    dispatch(resetPasskeyDeregisterState());
    navigate(-1);
  };

  const handleFailureClose = (): void => {
    dispatch(resetPasskeyDeregisterState());
  };

  const toggleFaq = (index: number): void => {
    setExpandedFaqs((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index]
    );
  };

  const isBusy = deregisterLoading || isDeregisterPending;
  const hasMultiplePasskeys = activePasskeys.length > 1;

  return (
    <main role="main" className="bg-bg-body py-4 md:px-8 px-4">
      <nav aria-label="Breadcrumb">
        <Breadcrumbs items={breadcrumbs} />
      </nav>

      <section
        className="w-full rounded-[15px] bg-white md:p-6 p-4 select-none caret-transparent"
        aria-labelledby="manage-passkey-title"
      >
        <h1
          id="manage-passkey-title"
          className="text-[22px] font-bold text-[#1F1F1F] mb-4"
        >
          {manage.title ?? 'Update passkey'}
        </h1>
        <p className="text-[15px] text-[#1F1F1F] leading-6 mb-3">
          {descriptions[0] ??
            'Simplify your sign-in options by using Face ID, PIN, fingerprint or a security key.'}
        </p>
        <p className="text-[15px] text-[#1F1F1F] leading-6 mb-6">
          {descriptions[1] ??
            'Each user can create their own sign-in option for extra security.'}
        </p>

        <h2 className="text-[16px] font-bold text-[#1F1F1F] mb-3">
          {`${passkeysTitle} (${activePasskeys.length})`}
        </h2>
        {listLoading ? (
          <div
            className="flex items-center justify-center py-8 mb-8"
            role="status"
            aria-label="Loading passkeys"
          >
            <div className="animate-spin ease-linear rounded-full border-4 border-t-4 h-8 w-8 border-[#DA291C]" />
          </div>
        ) : (
          <div
            className={`flex flex-col gap-3 mb-8 ${
              hasMultiplePasskeys ? 'max-h-[28rem] overflow-y-auto' : ''
            }`}
          >
            {activePasskeys.map((passkey, index) => {
              const providerIconKey = resolvePasskeyProviderIcon(
                passkey.passkeyProviderName
              );
              const providerIconUri = resolveAsset(assets, providerIconKey);
              const passkeyName = passkey.passkeyProviderName ?? '';
              return (
                <div
                  key={passkey.id || `${passkeyName}-${index}`}
                  className="bg-[#E9E9E9] rounded-md px-4 py-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                    <div className="flex flex-1 items-start gap-3 min-w-0">
                      {providerIconKey === 'icon_apple_logo' ? (
                        <PasskeyAppleIcon className="w-6 h-6 shrink-0 mt-0.5" />
                      ) : providerIconUri ? (
                        <img
                          src={providerIconUri}
                          alt=""
                          className="w-6 h-6 shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <p className="text-[16px] font-bold text-[#1F1F1F] mb-1">
                          {passkeyName}
                        </p>
                        <p className="text-[13px] text-[#666666] mb-0.5">
                          {`${dateCreatedLabel}: ${formatPasskeyTimestamp(
                            passkey.dateCreated
                          )}`}
                        </p>
                        <p className="text-[13px] text-[#666666]">
                          {`${lastUsedLabel}: ${formatPasskeyTimestamp(
                            passkey.lastUsed
                          )}`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 border-t border-[#B5B5B5] pt-4 md:mt-0 md:border-t-0 md:pt-0">
                      <div className="flex items-center justify-between gap-3 md:justify-end">
                        {showInfoIcon ? (
                          <div className="relative group">
                            <button
                              type="button"
                              className="flex items-center gap-2 bg-transparent border-0 p-0 text-[15px] font-semibold text-[#2B7DE1]"
                              aria-label={keyDetailsLabel}
                            >
                              <PasskeyInfoIcon />
                              <span className="md:hidden">{keyDetailsLabel}</span>
                            </button>
                            {infoIconContent ? (
                              <div
                                role="tooltip"
                                className="absolute bottom-full right-0 z-50 mb-1 hidden w-max max-w-[180px] rounded border border-gray-400 bg-gray-50 px-2 py-1 text-xs font-[400] text-black shadow-md group-hover:block"
                              >
                                {infoIconContent}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPasskeyId(passkey.id);
                            setRemoveModalVisible(true);
                          }}
                          disabled={isBusy}
                          className={`flex shrink-0 items-center justify-center gap-2 h-10 px-3 border border-[#DA291C] bg-white text-[14px] font-semibold text-[#DA291C] whitespace-nowrap disabled:opacity-50 ${
                            showInfoIcon ? '' : 'ml-auto'
                          }`}
                          aria-label={`${removeLabel} ${passkeyName}`}
                        >
                          <PasskeyTrashIcon />
                          {removeLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showMoreAbout ? (
          <>
            <h2 className="text-[17px] font-bold text-[#1F1F1F] mb-2">
              {moreAbout.title ?? 'More about passkeys'}
            </h2>
            {faqItems.map(
              (item: { question?: string; answer?: string }, index: number) => {
                const isExpanded = expandedFaqs.includes(index);
                const question = item.question ?? '';
                return (
                  <div
                    key={question || String(index)}
                    className="border-b border-[#E0E0E0] py-4"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(index)}
                      className="flex items-center justify-between w-full text-left"
                      aria-expanded={isExpanded}
                      aria-label={question}
                    >
                      <span className="flex-1 text-[16px] font-bold text-[#1F1F1F] pr-3">
                        {question}
                      </span>
                      <img
                        src={isExpanded ? assets?.up_arrow : assets?.down_arrow}
                        alt=""
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </button>
                    {isExpanded ? (
                      <p className="text-[14px] text-[#1F1F1F] leading-5 mt-3">
                        {item.answer}
                      </p>
                    ) : null}
                  </div>
                );
              }
            )}
          </>
        ) : null}

        {showCancelButton ? (
          <button
            type="button"
            onClick={() => navigate('/')}
            disabled={isBusy}
            className="w-full md:w-1/2 md:mx-auto h-[48px] py-[12px] bg-white flex items-center justify-center border-2 border-[#DA291C] mt-8 text-[16px] font-semibold text-[#DA291C] cursor-pointer"
            aria-label={cancelLabel}
          >
            {cancelLabel}
          </button>
        ) : null}
      </section>

      <CommanModal
        isOpen={removeModalVisible}
        onClose={() => setRemoveModalVisible(false)}
        onCloseIconClick={() => setRemoveModalVisible(false)}
        onExtraButtonClick={handleConfirmRemove}
        width="max-w-md"
        buttonClassName="flex-col-reverse"
        buttonText={deleteActions.cancel ?? cancelLabel}
        extraButtonText={removeLabel}
        jsxContent={
          <div>
            <h2 className="text-[20px] font-bold text-[#1F1F1F] mb-4 pr-8">
              {deleteModal.title ?? 'Remove this passkey?'}
            </h2>
            {(Array.isArray(deleteModal.description) &&
            deleteModal.description.length > 0
              ? deleteModal.description
              : [
                  'You will no longer be able to use this passkey to sign-in to MyRogers account.',
                  'You can create a new passkey at any time.',
                ]
            ).map((paragraph: string, index: number) => (
              <p
                key={index}
                className="text-[15px] text-[#1F1F1F] leading-6 mb-3"
              >
                {paragraph}
              </p>
            ))}
            <p className="text-[15px] text-[#1F1F1F] leading-6 mb-4">
              {deleteModal.supportInfo ??
                "For instructions on deleting passkeys from your device, visit your devices' help center and search for passkey support."}
            </p>
            {supportLinks.map((link: { label: string; url: string }) => (
              <a
                key={link.label}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center mb-3 text-[15px] text-[#1570EF] underline"
                aria-label={`${link.label}, opens in browser`}
              >
                {link.label}
                {externalLinkIconUri ? (
                  <img
                    src={externalLinkIconUri}
                    alt=""
                    className="w-3.5 h-3.5 ml-1"
                    aria-hidden="true"
                  />
                ) : null}
              </a>
            ))}
          </div>
        }
      />

      {otpStep === 'verify' && otpUserName && passkeyToken && (
        <PasskeyVerifyIdentityModal
          userName={otpUserName}
          passkeyToken={passkeyToken}
          onClose={requestOtpExit}
          onSendCodeSuccess={handleSendCodeSuccess}
        />
      )}

      {otpStep === 'code' && otpUserName && passkeyToken && (
        <PasskeyVerificationCodeModal
          selectedContact={selectedContact}
          preferenceType={preferenceType}
          userName={otpUserName}
          passkeyToken={passkeyToken}
          onBack={handleOtpValidationBack}
          onClose={requestOtpExit}
          onOtpSuccess={handleOtpVerified}
        />
      )}

      <CommanModal
        isOpen={exitModalVisible && Boolean(exitCopy)}
        onClose={() => setExitModalVisible(false)}
        onExtraButtonClick={handleOtpClose}
        closeIcon={false}
        width="max-w-md"
        buttonText={exitCopy?.cancel}
        extraButtonText={exitCopy?.ok}
        extrabuttonstyle="primary"
        jsxContent={
          <div>
            {exitCopy?.title ? (
              <h2 className="text-[20px] font-bold text-[#1F1F1F] mb-4">
                {exitCopy.title}
              </h2>
            ) : null}
            {exitCopy?.description ? (
              <p className="text-[15px] text-[#1F1F1F] leading-6">
                {exitCopy.description}
              </p>
            ) : null}
          </div>
        }
      />

      {(isDeregisterPending || deregisterLoading) && !success && !error ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          role="progressbar"
          aria-label="Removing passkey"
        >
          <div className="animate-spin ease-linear rounded-full border-4 border-t-4 border-white h-10 w-10 border-[#DA291C]"></div>
        </div>
      ) : null}

      <SuccessModel
        isOpen={!!success}
        title={resultModal.success ?? 'Passkey Removed'}
        subTitle="This passkey can no longer be used to sign in to your MyRogers account."
        successIcon={true}
        closeModal={handleSuccessClose}
        errorIcon={false}
        buttonText="Ok"
      />
      <SuccessModel
        isOpen={!!error}
        title={resultModal.error ?? 'Error removing passkey'}
        subTitle="There was an error removing this passkey. Please try again."
        successIcon={false}
        closeModal={handleFailureClose}
        errorIcon={true}
        buttonText="Ok"
      />
    </main>
  );
};

export default ManagePasskey;
